import { Paper, styled, Typography } from '@mui/material'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AppointmentType, EventType } from '../../constants'
import { Address, Customer, IssueCode, Recommendation, RecommendationIssue } from '../../types'
import { appointmentTypeToDisplayText } from '../../util/appointmentTypeToDisplayText'
import { useTrackReservationMissingParameters } from './useTrackReservationMissingParameters'

const OuterContainer = styled(Paper)`
  display: grid;
  max-width: 50rem;
  justify-self: center;
  width: 100%;
`

const InnerContainer = styled('div')`
  display: grid;
  gap: 1rem;
  margin: 1rem;
`

const IssueContainer = styled('div')`
  display: grid;
`

const issueCategory = {
  'customer.fields': `The following customer information is invalid or missing`,
  'customer.notFound': 'Customer not found',
  'customer.recommendations.notFound': 'No recommendations found for this customer',
  'lead.fields': 'The following lead information is missing',
  'lead.notFound': 'Lead not found or we are waiting on Salesforce lead data to process',
  'lead.recommendations.notFound': 'No recommendations found for this lead',
  'reservation.disabled': 'The reservation feature is disabled. Please use the existing appointment flow to book an appointment.',
  'reservation.error': 'An error has occurred while loading the reservation',
  'url.parameters': 'The following URL parameters are required',
  'appointmentType.notAvailableInState': 'Appointment type not available',
  'recommendation.issues': 'Sorry, we were unable to load provider recommendations',
} as const

const issueDetail = {
  'customer.address.country': 'Address country',
  'customer.address.state': 'Address state',
  'customer.salesforceId': 'Salesforce ID',
  'customer.shippingAddress.country': 'Shipping address country',
  'customer.shippingAddress.state': 'Shipping address state',
  'customer.timeZone': 'Customer time zone',
  'reservation.notFound': 'Reservation not found',
  'searchParam.appointment_type': 'appointment_type',
  'searchParam.journey': 'journey',
  'searchParam.lead_gid': 'lead_gid',
  'searchParam.opportunity_gid': 'opportunity_gid',
  'searchParam.time_zone': 'time_zone',
  'recommendation.issues.default': 'No recommendations available', //USER_IS_NOT_PROVIDER, INVALID_JOURNEY
  'recommendation.issues.license_expired': 'All licenses found are expired',
  'recommendation.issues.license_notFound': 'No license(s) found for state: {stateCode} ({state})',
  'recommendation.issues.resource_notFound': 'Provider(s) are improperly configured',
  'recommendation.issues.invalid_journey': 'Invalid journey supplied',
  'recommendation.issues.appointmentTypeNotAvailableInState': '{appointmentType} is not available in {state}',
  'recommendation.issues.package_type_mismatch': 'Unable to match provider(s) using previously shipped customer package type',
  APPOINTMENT_NOT_FOUND: 'Unable to find previous appointment(s) for this customer',
  COUNTRY_NOT_AVAILABLE: 'Country not supported',
} as const

type IssueDetail = keyof typeof issueDetail | string
type IssueCategory = keyof typeof issueCategory
type IssueSummary = Record<IssueCategory, IssueDetail[] | undefined>
interface IssueSummaryWithMetaData {
  summary: Partial<IssueSummary>
  metaData: Record<IssueDetail, MetaDetail>
}

interface RecommendationDataIssues {
  appointmentType?: AppointmentType
  customer?: Customer
  customerProfileAddress?: Address
  recommendations?: Recommendation
  isReservation?: boolean
}

interface MetaDetail {
  [key: string]: string
}

interface Props {
  children?: ReactNode
  eventType?: EventType
  summary: Partial<IssueSummary>
  summaryMetadata?: Record<IssueDetail, MetaDetail>
}

const fatalIssues = [IssueCode.APPOINTMENT_NOT_FOUND, IssueCode.COUNTRY_NOT_AVAILABLE]

const recommendationFatalIssues = (issue: RecommendationIssue) => {
  return fatalIssues.includes(issue.code)
}

const allRecommendationIssuesAreLicenseNotFound = (issues: { code: IssueCode }[]): boolean =>
  issues.length > 0 && issues.every((issue) => issue.code === IssueCode.LICENSE_NOT_FOUND)

const allRecommendationIssuesAreLicenseExpired = (issues: { code: IssueCode }[]): boolean =>
  issues.length > 0 && issues.every((issue) => issue.code === IssueCode.LICENSE_EXPIRED)

const allRecommendationIssuesAreResourceNotFound = (issues: { code: IssueCode }[]): boolean =>
  issues.length > 0 && issues.every((issue) => issue.code === IssueCode.RESOURCE_NOT_FOUND)

const allRecommendationIssuesArePackageTypeMismatch = (issues: { code: IssueCode }[]): boolean =>
  issues.length > 0 && issues.every((issue) => issue.code === IssueCode.PACKAGE_TYPE_MISMATCH)

const allRecommendationIssuesAreStateRestricted = (issues: { code: IssueCode }[]): boolean =>
  issues.length > 0 && issues.every((issue) => issue.code === IssueCode.APPOINTMENT_TYPE_NOT_AVAILABLE_IN_STATE)

const hasFatalIssues = (issues: { code: IssueCode }[]): boolean =>
  issues.length > 0 && issues.some((issue) => fatalIssues.includes(issue.code))

const determineRecommendationIssues = ({
  appointmentType,
  customer,
  customerProfileAddress,
  recommendations,
  isReservation,
}: RecommendationDataIssues): Partial<IssueSummaryWithMetaData> | undefined => {
  let stateCode
  let state
  let missingStateCode

  if (customerProfileAddress) {
    state = customerProfileAddress.state
  }

  if (customer) {
    stateCode = customer.shippingAddress?.stateCode
    state = customer.shippingAddress?.state
    missingStateCode = !stateCode ? 'customerStateCodeMissing' : undefined
  }

  if (!recommendations?.providers?.length && recommendations?.recommendationIssuesSummary?.length) {
    let filteredRecommendationIssues = [...recommendations.recommendationIssuesSummary]
    if (isReservation) {
      filteredRecommendationIssues = recommendations.recommendationIssuesSummary.filter((issue) => issue.code !== 'OPPORTUNITY_NOT_FOUND')
    }
    switch (true) {
      case hasFatalIssues(filteredRecommendationIssues):
        const fatalIssueCodes = filteredRecommendationIssues.filter(recommendationFatalIssues).map((issue) => issue.code)
        return { summary: { 'recommendation.issues': fatalIssueCodes as IssueDetail[] } }
      case allRecommendationIssuesAreLicenseExpired(filteredRecommendationIssues):
        return { summary: { 'recommendation.issues': ['recommendation.issues.license_expired'] } }
      case allRecommendationIssuesAreResourceNotFound(filteredRecommendationIssues):
        return { summary: { 'recommendation.issues': ['recommendation.issues.resource_notFound'] } }
      case allRecommendationIssuesAreLicenseNotFound(filteredRecommendationIssues):
        const stateCode = filteredRecommendationIssues.find((issue) => issue.code === 'LICENSE_NOT_FOUND')?.entity.value
        return {
          summary: { 'recommendation.issues': ['recommendation.issues.license_notFound'] },
          metaData: {
            'recommendation.issues.license_notFound': {
              stateCode: stateCode ?? missingStateCode ?? '',
              state: state ?? 'customerStateNameMissing',
            },
          },
        }
      case allRecommendationIssuesAreStateRestricted(filteredRecommendationIssues): {
        const restrictedStateCode = filteredRecommendationIssues[0]?.entity?.value
        return {
          summary: { 'appointmentType.notAvailableInState': ['recommendation.issues.appointmentTypeNotAvailableInState'] },
          metaData: {
            'recommendation.issues.appointmentTypeNotAvailableInState': {
              appointmentType: (appointmentType && appointmentTypeToDisplayText[appointmentType]) || 'This appointment type',
              state: state || restrictedStateCode || 'the customer state',
            },
          },
        }
      }
      case allRecommendationIssuesArePackageTypeMismatch(filteredRecommendationIssues):
        return { summary: { 'recommendation.issues': ['recommendation.issues.package_type_mismatch'] } }
      default:
        return { summary: { 'customer.recommendations.notFound': [] } }
    }
  }
  return
}

const Issue = ({
  category,
  details,
  summaryMetadata,
}: {
  category: IssueCategory
  details: IssueDetail[]
  summaryMetadata?: Record<IssueDetail, MetaDetail>
}) => {
  const { t } = useTranslation()

  return (
    <IssueContainer>
      <Typography variant="h2">{t(category, { defaultValue: issueCategory[category] ?? category })}</Typography>
      {details.length > 0 && (
        <ul>
          {details.map((detail) => {
            const metaDetail = summaryMetadata?.[detail as keyof typeof issueDetail] ?? {}
            const baseText = t(detail, {
              replace: metaDetail,
              defaultValue: (issueDetail[detail as keyof typeof issueDetail] as string) ?? detail,
            })
            const result = Object.entries(metaDetail).reduce((currentText, [key, value]) => {
              return currentText.replace(`{${key}}`, value)
            }, baseText)
            return <li key={detail}>{result}</li>
          })}
        </ul>
      )}
    </IssueContainer>
  )
}

const Issues = ({ children, eventType, summary, summaryMetadata }: Props) => {
  const invalidParameters = useMemo(
    () =>
      Object.values(summary).reduce((acc, details) => {
        if (details?.length) {
          acc.push(
            ...details.map((detail) => {
              return issueDetail[detail as keyof typeof issueDetail]
            })
          )
        }

        return acc.filter(Boolean)
      }, [] as (typeof issueDetail)[keyof typeof issueDetail][]),
    [summary]
  )

  useTrackReservationMissingParameters({ eventType, invalidParameters })

  return (
    <OuterContainer elevation={3}>
      <InnerContainer>
        {Object.entries(summary).map(([title, details]) => (
          <Issue key={title} category={title as IssueCategory} details={details ?? []} summaryMetadata={summaryMetadata} />
        ))}
        {children && (
          <>
            <div data-x="spacer" />
            {children}
          </>
        )}
      </InnerContainer>
    </OuterContainer>
  )
}

export { determineRecommendationIssues, Issues }
export type { IssueCategory, IssueDetail }
